import type {
  InventorySearchQuery,
  InventorySearchResponse,
  InventoryStockItem,
} from "@medicorepay/shared";
import { useCallback, useEffect, useRef, useState } from "react";

export interface InventoryStockViewProps {
  searchInventory: (
    query: InventorySearchQuery,
  ) => Promise<InventorySearchResponse>;
}

const PAGE_SIZE = 25;
const EXPIRY_WINDOW_DAYS = 30;

type ExpiryWindow = "ALL" | "30_DAYS";

function moneyFromPaise(value: string) {
  return (Number(value) / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function quantity(value: string) {
  return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function date(value: string | null) {
  return value ? new Date(value).toLocaleDateString("en-IN") : "No expiry";
}

function statusLabel(status: InventoryStockItem["status"]) {
  if (status === "OUT_OF_STOCK") return "Out of stock";
  if (status === "LOW") return "Low stock";
  return "In stock";
}

function statusClass(status: InventoryStockItem["status"]) {
  if (status === "OUT_OF_STOCK") return "bg-red-100 text-red-800";
  if (status === "LOW") return "bg-orange-100 text-orange-800";
  return "bg-green-100 text-green-800";
}

export function InventoryStockView({
  searchInventory,
}: InventoryStockViewProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] =
    useState<NonNullable<InventorySearchQuery["status"]>>("ALL");
  const [expiryWindow, setExpiryWindow] = useState<ExpiryWindow>("ALL");
  const [sortBy, setSortBy] =
    useState<NonNullable<InventorySearchQuery["sortBy"]>>("name");
  const [sortOrder, setSortOrder] =
    useState<NonNullable<InventorySearchQuery["sortOrder"]>>("asc");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<InventorySearchResponse | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastDurationMs, setLastDurationMs] = useState<number | null>(null);
  const requestSequence = useRef(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    const startedAt = performance.now();
    setLoading(true);
    setError(null);
    try {
      const response = await searchInventory({
        search: debouncedSearch || undefined,
        category: category.trim() || undefined,
        status,
        sortBy,
        sortOrder,
        page,
        limit: PAGE_SIZE,
        through:
          expiryWindow === "30_DAYS"
            ? new Date(
                Date.now() + EXPIRY_WINDOW_DAYS * 86_400_000,
              ).toISOString()
            : undefined,
      });
      if (sequence !== requestSequence.current) return;
      setResult(response);
      setLastDurationMs(Math.round(performance.now() - startedAt));
    } catch (requestError) {
      if (sequence !== requestSequence.current) return;
      const candidate = requestError as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(
        candidate.response?.data?.message ||
          candidate.message ||
          "Unable to load stock",
      );
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [
    category,
    debouncedSearch,
    expiryWindow,
    page,
    searchInventory,
    sortBy,
    sortOrder,
    status,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = result?.summary;
  const pagination = result?.pagination;
  const toggleLots = (productId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Overview</h1>
          <p className="mt-1 text-sm text-gray-500">
            Live ledger balances, batches, expiry and stock value
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastDurationMs !== null && (
            <span className="text-xs text-gray-500">
              Server response {lastDurationMs} ms
            </span>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Stat label="Products" value={summary?.totalProducts ?? 0} />
        <Stat
          label="Stock value at MRP"
          value={`₹${moneyFromPaise(summary?.totalStockValuePaise ?? "0")}`}
        />
        <Stat
          label="Low / out of stock"
          value={`${summary?.lowStockProducts ?? 0} / ${summary?.outOfStockProducts ?? 0}`}
          tone="warning"
        />
        <Stat
          label="Expiring (30 days) / expired lots"
          value={`${summary?.expiringLots ?? 0} / ${summary?.expiredLots ?? 0}`}
          tone="danger"
        />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-[minmax(240px,1fr)_180px_180px_190px_160px]">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, SKU, barcode or manufacturer"
            className="rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
          />
          <input
            aria-label="Stock category"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setPage(1);
            }}
            placeholder="Category"
            className="rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
          />
          <select
            aria-label="Stock status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as typeof status);
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="ALL">All stock</option>
            <option value="IN_STOCK">In stock</option>
            <option value="LOW">Low stock</option>
            <option value="OUT_OF_STOCK">Out of stock</option>
          </select>
          <select
            aria-label="Expiry window"
            value={expiryWindow}
            onChange={(event) => {
              setExpiryWindow(event.target.value as ExpiryWindow);
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="ALL">All expiry dates</option>
            <option value="30_DAYS">Expiring in 30 days</option>
          </select>
          <select
            aria-label="Sort stock"
            value={`${sortBy}:${sortOrder}`}
            onChange={(event) => {
              const [nextSort, nextOrder] = event.target.value.split(":") as [
                typeof sortBy,
                typeof sortOrder,
              ];
              setSortBy(nextSort);
              setSortOrder(nextOrder);
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
            <option value="available:desc">Quantity high</option>
            <option value="available:asc">Quantity low</option>
            <option value="stockValue:desc">Value high</option>
            <option value="expiry:asc">Expiry soonest</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="font-semibold text-gray-900">Stock levels</h2>
          <span className="text-sm text-gray-500">
            {pagination?.total ?? 0} matching products
            {expiryWindow === "30_DAYS" ? " expiring within 30 days" : ""}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Batch / expiry</th>
                <th className="px-4 py-3 text-right">Available</th>
                <th className="px-4 py-3 text-right">Reorder</th>
                <th className="px-4 py-3 text-right">Value</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && !result ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    Loading live stock…
                  </td>
                </tr>
              ) : !result?.items.length ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    No products match the server filters.
                  </td>
                </tr>
              ) : (
                result.items.map((item) => (
                  <StockRows
                    key={item.productId}
                    item={item}
                    expanded={expanded.has(item.productId)}
                    onToggle={() => toggleLots(item.productId)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= pagination.totalPages || loading}
                onClick={() =>
                  setPage((value) => Math.min(pagination.totalPages, value + 1))
                }
                className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "normal",
}: {
  label: string;
  value: string | number;
  tone?: "normal" | "warning" | "danger";
}) {
  const color =
    tone === "danger"
      ? "text-red-600"
      : tone === "warning"
        ? "text-orange-600"
        : "text-gray-900";
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function StockRows({
  item,
  expanded,
  onToggle,
}: {
  item: InventoryStockItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3">
          <div className="font-medium text-gray-900">{item.name}</div>
          <div className="text-xs text-gray-500">
            {item.sku} · {item.manufacturer || "No manufacturer"}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">
          <button
            type="button"
            onClick={onToggle}
            className="font-medium text-blue-700 hover:underline"
          >
            {item.lots.length} {item.lots.length === 1 ? "batch" : "batches"}
          </button>
          <div className="text-xs text-gray-500">
            Next: {date(item.nextExpiryDate)}
          </div>
        </td>
        <td className="px-4 py-3 text-right font-semibold tabular-nums">
          {quantity(item.available)} {item.unit}
        </td>
        <td className="px-4 py-3 text-right text-sm tabular-nums">
          {item.reorderLevel ? quantity(item.reorderLevel) : "—"}
        </td>
        <td className="px-4 py-3 text-right text-sm tabular-nums">
          ₹{moneyFromPaise(item.stockValuePaise)}
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClass(item.status)}`}
          >
            {statusLabel(item.status)}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-slate-50 px-6 py-3">
            {item.lots.length ? (
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {item.lots.map((lot) => (
                  <div
                    key={lot.lotId}
                    className="rounded border border-slate-200 bg-white p-3 text-xs"
                  >
                    <div className="font-semibold text-gray-900">
                      Batch {lot.batchNumber}
                    </div>
                    <div className="mt-1 text-gray-600">
                      Expiry {date(lot.expiryDate)}
                    </div>
                    <div className="mt-1 flex justify-between text-gray-600">
                      <span>{quantity(lot.available)} available</span>
                      <span>MRP ₹{moneyFromPaise(lot.mrpPaise)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-sm text-gray-500">No ledger lots.</span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
